// Today's Insight content library — seed of 30 short, evidence-based
// educational nuggets, grouped by symptom category. One is shown per day
// on the app home screen, biased to the user's most-logged category in
// the last 7 days.
//
// Editorial rules for every entry:
// - One concrete fact + one practical, low-risk action.
// - Educational only. Never prescriptive medical advice.
// - Plain English, <= 240 chars.

import { localDateKey } from "./dailyLogs";

export type InsightCategory =
  | "mood"
  | "sleep"
  | "hot_flashes"
  | "energy"
  | "brain_fog"
  | "anxiety"
  | "cramps"
  | "general";

export interface Insight {
  id: string;
  category: InsightCategory;
  text: string;
}

export const INSIGHTS: Insight[] = [
  // Mood (4)
  { id: "mood-1", category: "mood", text: "Mood symptoms often track with sleep quality. A wind-down routine of 30 minutes — lights low, no screens — can soften next-day irritability." },
  { id: "mood-2", category: "mood", text: "Estrogen shifts during perimenopause can amplify mood swings. Logging mood alongside cycle day helps your doctor see the pattern, not just the snapshot." },
  { id: "mood-3", category: "mood", text: "Short bursts of daylight (10 minutes outside, morning) are linked to better mood regulation through the day." },
  { id: "mood-4", category: "mood", text: "Caffeine after 2pm can quietly raise next-day anxiety and low mood. Try shifting your last cup earlier for a week and watch the trend." },

  // Sleep (4)
  { id: "sleep-1", category: "sleep", text: "Night sweats and waking at 3am are a classic perimenopause pattern — not 'just stress.' Logging it gives your doctor something concrete to act on." },
  { id: "sleep-2", category: "sleep", text: "A cool bedroom (around 65°F / 18°C) reduces the intensity of nighttime hot flashes for many women." },
  { id: "sleep-3", category: "sleep", text: "Alcohol fragments sleep even when it helps you fall asleep faster. One drink-free week often shows up clearly in your trend." },
  { id: "sleep-4", category: "sleep", text: "Consistent wake time matters more than bedtime for menopausal sleep. Pick a wake time you can keep on weekends too." },

  // Hot flashes (4)
  { id: "hf-1", category: "hot_flashes", text: "Hot flashes often peak in the late-luteal phase of your cycle. Layered clothing and a bedside fan make a real, measurable difference." },
  { id: "hf-2", category: "hot_flashes", text: "Spicy food, alcohol, and stress are the three most-reported hot flash triggers. Logging them next to your flashes reveals which one is yours." },
  { id: "hf-3", category: "hot_flashes", text: "Cold water on the wrists or back of the neck at the first warning can shorten a hot flash. Small, but it adds up over a day." },
  { id: "hf-4", category: "hot_flashes", text: "Frequency and time-of-day of hot flashes are what tell your doctor whether HRT is working. A simple count beats a vague 'a lot.'" },

  // Energy (4)
  { id: "energy-1", category: "energy", text: "Afternoon energy crashes during perimenopause are often blood-sugar driven. Pairing carbs with protein at lunch flattens the curve." },
  { id: "energy-2", category: "energy", text: "Even 10 minutes of walking after a meal improves glucose response and same-day energy. The smallest dose still counts." },
  { id: "energy-3", category: "energy", text: "Iron and ferritin can dip in the heavy-bleed years of perimenopause. If energy is consistently low, ask your doctor for a ferritin check." },
  { id: "energy-4", category: "energy", text: "Strength training twice a week is linked to better energy and bone density after 40 — more than cardio alone." },

  // Brain fog (4)
  { id: "fog-1", category: "brain_fog", text: "Brain fog in perimenopause is real and measurable — and it usually improves with better sleep, not more effort." },
  { id: "fog-2", category: "brain_fog", text: "Writing it down beats remembering it. A simple capture habit (one notebook, one app) reduces the cognitive load fog creates." },
  { id: "fog-3", category: "brain_fog", text: "Hydration matters more than you'd expect. Mild dehydration shows up first as foggy thinking, not thirst." },
  { id: "fog-4", category: "brain_fog", text: "Brain fog often improves within weeks of starting effective HRT. If it doesn't, that's important data — log it." },

  // Anxiety (4)
  { id: "anx-1", category: "anxiety", text: "New-onset anxiety in your 40s is one of the most-missed perimenopause symptoms. Tracking it makes the hormonal pattern visible." },
  { id: "anx-2", category: "anxiety", text: "Slow exhales (4 seconds in, 6 seconds out, for 2 minutes) activate the parasympathetic system and reliably lower acute anxiety." },
  { id: "anx-3", category: "anxiety", text: "Alcohol commonly worsens next-morning anxiety in perimenopause — even amounts that didn't bother you a few years ago." },
  { id: "anx-4", category: "anxiety", text: "Magnesium glycinate before bed is well-tolerated and linked to lower anxiety and better sleep in midlife. Worth asking your doctor about." },

  // Cramps (3)
  { id: "cramp-1", category: "cramps", text: "Perimenopause cycles can swing between very light and very heavy. Logging flow and cramps together helps spot the shift." },
  { id: "cramp-2", category: "cramps", text: "Heating pads and gentle movement reliably reduce cramp intensity — both work better than waiting it out." },
  { id: "cramp-3", category: "cramps", text: "Heavy or unpredictable bleeding past 40 deserves a conversation with your doctor, not patience. Your log is the conversation starter." },

  // General (3)
  { id: "gen-1", category: "general", text: "The most powerful thing you bring to a doctor's appointment is data they can see. Even 2 weeks of logging changes the conversation." },
  { id: "gen-2", category: "general", text: "Menopause is not one event — it's a transition that averages 7-10 years. Tracking turns that long arc into a story you can act on." },
  { id: "gen-3", category: "general", text: "You're not imagining it. Symptoms in perimenopause are real, hormonal, and treatable — and your data is the proof." },
];

export const INSIGHT_DISCLAIMER =
  "Educational only. Talk to your doctor for medical advice.";

// Map raw `symptom_name` strings logged in daily_logs/symptom_events to
// our insight categories. Anything unmapped falls into `general`.
const NAME_TO_CATEGORY: Record<string, InsightCategory> = {
  "Hot flashes": "hot_flashes",
  "Night sweats": "hot_flashes",
  "Mood": "mood",
  "Mood swings": "mood",
  "Irritability": "mood",
  "Low mood": "mood",
  "Sleep": "sleep",
  "Insomnia": "sleep",
  "Poor sleep": "sleep",
  "Energy": "energy",
  "Fatigue": "energy",
  "Low energy": "energy",
  "Brain fog": "brain_fog",
  "Memory": "brain_fog",
  "Anxiety": "anxiety",
  "Cramps": "cramps",
  "Period cramps": "cramps",
};

export function categoryForSymptom(name: string): InsightCategory {
  return NAME_TO_CATEGORY[name] ?? "general";
}

/**
 * Given recent log rows ({ log_date, symptom_name }), return the most-logged
 * category over the last 7 days (inclusive of today). Falls back to "general".
 */
export function topCategory(
  logs: { log_date: string; symptom_name: string }[],
  todayKey: string,
): InsightCategory {
  if (!logs?.length) return "general";
  // Build set of last 7 date keys.
  const cutoff = new Date(`${todayKey}T00:00:00`);
  cutoff.setDate(cutoff.getDate() - 6);
  const cutoffKey = localDateKey(cutoff);

  const counts = new Map<InsightCategory, number>();
  for (const row of logs) {
    if (!row.log_date || row.log_date < cutoffKey || row.log_date > todayKey) continue;
    const cat = categoryForSymptom(row.symptom_name);
    if (cat === "general") continue;
    counts.set(cat, (counts.get(cat) ?? 0) + 1);
  }
  let best: InsightCategory = "general";
  let bestCount = 0;
  for (const [cat, n] of counts) {
    if (n > bestCount) {
      best = cat;
      bestCount = n;
    }
  }
  return best;
}

/**
 * Deterministic daily insight: rotate through insights for the chosen
 * category by day-of-year, so the same user sees the same insight all day
 * but a fresh one tomorrow. Falls back to the general pool if the category
 * has no entries.
 */
export function insightForDay(category: InsightCategory, dateKey: string): Insight {
  const pool = INSIGHTS.filter((i) => i.category === category);
  const finalPool = pool.length > 0 ? pool : INSIGHTS.filter((i) => i.category === "general");
  // Day-of-year style index from YYYY-MM-DD.
  const d = new Date(`${dateKey}T00:00:00`);
  const start = new Date(d.getFullYear(), 0, 0);
  const diff = (d.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  const idx = Math.floor(diff) % finalPool.length;
  return finalPool[idx];
}
